import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Chip,
} from '@mui/material';
import { ArrowBack, Refresh, CheckCircleOutline, WarningAmberRounded } from '@mui/icons-material';
import { PageHeader, TableSurface, headerCellSx, bodyRowSx, ErrorState } from '../../components/common/pageKit';
import { assetService } from '../../services/assetService';
import { fmtMoney } from './assetFormat';

const AssetReconciliationPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<{ lines: any[]; allMatched: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    assetService.getGlReconciliation()
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load reconciliation'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/assets')}>Assets</Button>
      </Stack>
      <PageHeader title="Asset ↔ GL Reconciliation" subtitle="Fixed-asset register balances vs the posted general ledger."
        actions={<Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>} />

      {data && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {data.allMatched
            ? <><CheckCircleOutline color="success" /><Typography sx={{ fontWeight: 700 }}>Register and GL are reconciled</Typography></>
            : <><WarningAmberRounded sx={{ color: '#d97706' }} /><Typography sx={{ fontWeight: 700 }}>Differences found — review below</Typography></>}
        </Paper>
      )}

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={load} /></Box>}

      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Component', 'Account', 'Asset Register', 'GL Balance', 'Difference', 'Status'].map((h, i) => (
                  <TableCell key={h} sx={headerCellSx} align={i >= 2 && i <= 4 ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : (data?.lines || []).map((l) => (
                <TableRow key={l.accountCode} sx={bodyRowSx as any}>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{l.label}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{l.accountCode}</Typography></TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.register)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.gl)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: l.matched ? 'text.secondary' : '#dc2626' }}>{fmtMoney(l.difference)}</TableCell>
                  <TableCell><Chip size="small" label={l.matched ? 'Matched' : 'Unreconciled'} color={l.matched ? 'success' : 'error'} variant={l.matched ? 'filled' : 'outlined'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default AssetReconciliationPage;
